// API helper functions for dashboard data
// This connects to the backend API server

import { getCurrentUserDepartment } from "utils/userUtils";
import { API_BASE_URL } from "./base";

// API helper function to make HTTP requests
export const apiRequest = async (url, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

// Mock data for fallback when database is not available
const mockDashboardStats = {
  properties: { total: 302, active: 265, inactive: 37 },
  invoices: { total: 2300, paid: 2209, unpaid: 91 },
  financial: { totalOutstanding: 45600, totalPayments: 234000 },
};

const mockMonthlyArrears = [
  45000, 52000, 48000, 61000, 55000, 67000, 59000, 63000, 58000, 72000, 69000, 74000,
];

export const apiGetDashboardStats = async () => {
  try {
    // Get current user's department
    const userDepartment = getCurrentUserDepartment();

    // Get real data from the backend API with department filter
    const stats = await apiRequest(
      `/dashboard/stats?department=${encodeURIComponent(userDepartment)}`
    );
    return stats;
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    return mockDashboardStats;
  }
};

export const apiGetMonthlyArrearsData = async () => {
  try {
    // Get current user's department
    const userDepartment = getCurrentUserDepartment();

    // Get real data from the backend API with department filter
    const data = await apiRequest(
      `/dashboard/monthly-arrears?department=${encodeURIComponent(userDepartment)}`
    );
    return data;
  } catch (error) {
    console.error("Error fetching monthly arrears data:", error);
    return mockMonthlyArrears;
  }
};

export const apiGetCriticalArrears = async () => {
  try {
    // Get current user's department
    const userDepartment = getCurrentUserDepartment();

    // Get real data from the backend API with department filter
    const data = await apiRequest(
      `/dashboard/critical-arrears?department=${encodeURIComponent(userDepartment)}`
    );
    return data;
  } catch (error) {
    console.error("Error fetching critical arrears:", error);
    return mockCriticalArrears;
  }
};

// Get UEU active premis count
export const apiGetUEUActivePremisCount = async () => {
  try {
    const response = await apiRequest("/dashboard/ueu-active-premis-count");
    // The backend returns { count: number }
    return response.count;
  } catch (error) {
    console.error("Error fetching UEU active premis count:", error);
    return 0;
  }
};

// Get last auto-generation date per zone for the current department
export const apiGetJanaTerakhir = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const data = await apiRequest(
      `/dashboard/jana-terakhir?department=${encodeURIComponent(userDepartment)}`
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching jana terakhir data:", error);
    return [];
  }
};

// Get total for 'Open Inbois (Pelbagai)'
export const apiGetOpenInboisPelbagaiTotal = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const response = await apiRequest(
      `/dashboard/open-inbois-pelbagai-total?department=${encodeURIComponent(userDepartment)}`
    );
    return Number(response.total || 0);
  } catch (error) {
    console.error("Error fetching Open Inbois (Pelbagai) total:", error);
    return 0;
  }
};

// Get counts of distinct penyewa (nama) overall and active for current department
export const apiGetPenyewaCounts = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const response = await apiRequest(
      `/dashboard/penyewa-count?department=${encodeURIComponent(userDepartment)}`
    );
    return { total: Number(response.total || 0), active: Number(response.active || 0) };
  } catch (error) {
    console.error("Error fetching penyewa counts:", error);
    return { total: 0, active: 0 };
  }
};

// Get notis summary (counts and totals per stage)
export const apiGetNotisSummary = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const response = await apiRequest(
      `/dashboard/notis-summary?department=${encodeURIComponent(userDepartment)}`
    );
    // Normalize numbers
    return {
      notisPertama: {
        count: Number(response?.notisPertama?.count || 0),
        total: Number(response?.notisPertama?.total || 0),
      },
    };
  } catch (error) {
    console.error("Error fetching notis summary:", error);
    return {
      notisPertama: { count: 0, total: 0 },
    };
  }
};

// ============================================================================
// KEW DASHBOARD API FUNCTIONS - Terimaan-based metrics
// ============================================================================

// Get terimaan summary (total amount for KEW dashboard)
export const apiGetTerimaanSummary = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const response = await apiRequest(
      `/dashboard/terimaan-summary?department=${encodeURIComponent(userDepartment)}`
    );
    return { totalOutstanding: Number(response?.totalTerimaan || 0) };
  } catch (error) {
    console.error("Error fetching terimaan summary:", error);
    return { totalOutstanding: 0 };
  }
};

// Get monthly terimaan data (last 12 months for trend chart)
export const apiGetMonthlyTerimaanData = async () => {
  try {
    const userDepartment = getCurrentUserDepartment();
    const data = await apiRequest(
      `/dashboard/monthly-terimaan?department=${encodeURIComponent(userDepartment)}`
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching monthly terimaan data:", error);
    return [];
  }
};

// Get terimaan terakhir per zone (for all departments with Bahagian column)
export const apiGetTerimaanTerakhir = async () => {
  try {
    const response = await apiRequest("/dashboard/terimaan-terakhir");
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching terimaan terakhir data:", error);
    return [];
  }
};

// Get list of all available bahagian (for KEW Dashboard dropdown)
export const apiGetBahagianList = async () => {
  try {
    const response = await apiRequest("/dashboard/bahagian-list");
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error("Error fetching bahagian list:", error);
    return [];
  }
};

// Get tunggakan summary by bahagian (for KEW Dashboard)
export const apiGetTunggakanByBahagian = async (bahagian) => {
  try {
    if (!bahagian) throw new Error("Bahagian parameter is required");
    const response = await apiRequest(`/dashboard/tunggakan-by-bahagian?bahagian=${encodeURIComponent(bahagian)}`);
    return response;
  } catch (error) {
    console.error("Error fetching tunggakan by bahagian:", error);
    return { bahagian, bil_inbois: 0, jumlah: 0 };
  }
};

// Export all API functions
export default {
  getDashboardStats: apiGetDashboardStats,
  getMonthlyArrearsData: apiGetMonthlyArrearsData,
  getCriticalArrears: apiGetCriticalArrears,
  getJanaTerakhir: apiGetJanaTerakhir,
  getUEUActivePremisCount: apiGetUEUActivePremisCount,
  getOpenInboisPelbagaiTotal: apiGetOpenInboisPelbagaiTotal,
  getPenyewaCounts: apiGetPenyewaCounts,
  getNotisSummary: apiGetNotisSummary,
  getTerimaanSummary: apiGetTerimaanSummary,
  getMonthlyTerimaanData: apiGetMonthlyTerimaanData,
  getTerimaanTerakhir: apiGetTerimaanTerakhir,
  getBahagianList: apiGetBahagianList,
  getTunggakanByBahagian: apiGetTunggakanByBahagian,
};
